/**
 * Predimensionamiento de zapatas — área por σ_adm del suelo.
 *
 *   Az_required = P_servicio / σ_adm        (E.050)
 *   Peralte Hf:
 *     Pequeña (P < 30 tf):  Hf ≈ 50 cm
 *     Mediana (30–80 tf):    Hf ≈ 60 cm
 *     Grande (> 80 tf):      Hf ≈ 70-90 cm
 *
 * Las cuatro variantes (aislada, corrida, combinada, conectada) usan
 * formulaciones equivalentes — sólo varía la forma del cálculo del área.
 */

import { useState } from 'react';
import { PredimCard, Group, NumberRow, ResultRow, FormulaNote, roundTo } from './shared';
import { FootingDiagram, DiagramPanel, View2D3DToggle } from './diagrams';
import { Footing3D } from './views3d';
import { PredimToolbar } from './PredimToolbar';
import type { DesignFull } from '@/features/designs/types';

type Variant = 'isolated' | 'strip' | 'combined' | 'connected';

interface PredimFootingProps {
  variant: Variant;
  initialInputs?: {
    P?: number;
    P2?: number;
    sigma_adm?: number;
    bc?: number;
    hc?: number;
    L_cols?: number;
  } | null;
  existing?: DesignFull | null;
}

const VARIANT_PRESETS: Record<Variant, { label: string; descr: string }> = {
  isolated: {
    label: 'Zapata aislada',
    descr: 'Una columna centrada. Az = P / σ_adm. Lados ≈ √Az (cuadrada) o ratio que reproduce la columna.',
  },
  strip: {
    label: 'Zapata corrida',
    descr: 'Bajo muro continuo. Ancho B = (P por metro) / σ_adm.',
  },
  combined: {
    label: 'Zapata combinada',
    descr: 'Bajo dos columnas. Área total = (P1+P2) / σ_adm. Centrar sobre el centroide de cargas.',
  },
  connected: {
    label: 'Zapata conectada',
    descr: 'Medianera + interior unidas por viga. Az_medianera = Pm/σ_adm, Az_interior = Pi/σ_adm.',
  },
};

export function PredimFooting({ variant, initialInputs, existing }: PredimFootingProps) {
  const preset = VARIANT_PRESETS[variant];
  const [P, setP] = useState(initialInputs?.P ?? 50_000);            // kgf (or kgf/m for strip)
  const [P2, setP2] = useState(initialInputs?.P2 ?? 50_000);          // for combined/connected
  const [sigma_adm, setSigmaAdm] = useState(initialInputs?.sigma_adm ?? 1.5); // kgf/cm²
  const [bc, setBc] = useState(initialInputs?.bc ?? 30);              // for isolated/combined column dim
  const [hc, setHc] = useState(initialInputs?.hc ?? 30);
  // For combined: distance between cols
  const [L_cols, setLCols] = useState(initialInputs?.L_cols ?? 400);

  const sigma_kgf_m2 = sigma_adm * 10_000;  // kgf/cm² → kgf/m²

  let result: {
    fields: Array<[string, string]>;
    mainResult: string;
    comments: string[];
    dims: { Lx: number; Ly: number; Hf: number; bc?: number; hc?: number; L_cols?: number; Lx2?: number; Ly2?: number };
  };

  if (variant === 'isolated') {
    const Az_required_m2 = P / sigma_kgf_m2;
    // Make it a rectangle that reproduces the column aspect ratio
    const ratio = hc / bc;
    // side_x · side_y = Az; side_y / side_x = ratio  → side_x = √(Az / ratio)
    const side_x_m = Math.sqrt(Az_required_m2 / ratio);
    const side_y_m = side_x_m * ratio;
    const Lx_cm = roundTo(side_x_m * 100, 10);
    const Ly_cm = roundTo(side_y_m * 100, 10);
    const Hf = P / 1000 < 30 ? 50 : P / 1000 < 80 ? 60 : 70;
    result = {
      fields: [
        ['Área requerida', `${Az_required_m2.toFixed(2)} m²`],
        ['Lx sugerido', `${Lx_cm} cm`],
        ['Ly sugerido', `${Ly_cm} cm`],
        ['Hf sugerido', `${Hf} cm`],
      ],
      mainResult: `Lx × Ly = ${Lx_cm} × ${Ly_cm} cm  ·  Hf = ${Hf} cm`,
      comments: [
        `P / σ_adm = ${(P / 1000).toFixed(1)} tf / ${sigma_adm.toFixed(2)} kgf/cm² = ${Az_required_m2.toFixed(2)} m²`,
        'La relación Lx:Ly reproduce bc:hc para que la zapata sea simétrica respecto a la columna.',
      ],
      dims: { Lx: Lx_cm, Ly: Ly_cm, Hf, bc, hc },
    };
  } else if (variant === 'strip') {
    const B_required_m = P / sigma_kgf_m2;     // P here is per metre of wall (kgf/m)
    const B_cm = roundTo(B_required_m * 100, 10);
    const Hf = 50;
    const tw_typical = 15;  // typical confined-masonry wall thickness
    result = {
      fields: [
        ['B requerido', `${B_required_m.toFixed(2)} m (${B_cm} cm)`],
        ['Hf sugerido', `${Hf} cm`],
      ],
      mainResult: `B = ${B_cm} cm  ·  Hf = ${Hf} cm`,
      comments: [
        `B = (P/m) / σ_adm = ${(P / 1000).toFixed(2)} tf/m / ${sigma_adm.toFixed(2)} kgf/cm² = ${B_required_m.toFixed(3)} m`,
        'Para tw del muro mucho menor que B, considerar zapata aislada o losa.',
      ],
      dims: { Lx: 100, Ly: B_cm, Hf, bc: tw_typical },
    };
  } else if (variant === 'combined') {
    const P_total = P + P2;
    const Az_required_m2 = P_total / sigma_kgf_m2;
    // Footing length should comfortably span both columns. Conservatively
    // make L = L_cols + 60 cm (30 cm cantilever on each side).
    const L_cm = roundTo(L_cols + 60, 10);
    const B_cm = roundTo((Az_required_m2 * 10_000) / L_cm, 10);
    const Hf = P_total / 1000 < 60 ? 60 : 70;
    // Load centroid from left column (xc = Pu1·0 + Pu2·L_cols) / Pu_tot
    const x_centroid = (P2 * L_cols) / P_total;
    result = {
      fields: [
        ['P total', `${(P_total / 1000).toFixed(1)} tonf`],
        ['Área requerida', `${Az_required_m2.toFixed(2)} m²`],
        ['L sugerido', `${L_cm} cm (L_cols + 60)`],
        ['B sugerido', `${B_cm} cm`],
        ['Hf sugerido', `${Hf} cm`],
        ['Centroide de cargas (desde C1)', `${x_centroid.toFixed(1)} cm`],
      ],
      mainResult: `L × B = ${L_cm} × ${B_cm} cm  ·  Hf = ${Hf} cm`,
      comments: [
        'Centrar la zapata de modo que su centro coincida con el centroide de cargas para presión uniforme.',
        `Eje de simetría a x = L/2 = ${(L_cm / 2).toFixed(0)} cm desde el borde izquierdo.`,
      ],
      dims: { Lx: L_cm, Ly: B_cm, Hf, bc, hc, L_cols },
    };
  } else {
    // connected: 2 zapatas independientes
    const Az1 = P / sigma_kgf_m2;
    const Az2 = P2 / sigma_kgf_m2;
    const side1_m = Math.sqrt(Az1);
    const side2_m = Math.sqrt(Az2);
    const Lx1 = roundTo(side1_m * 100 * 0.6, 10);   // medianera más alargada
    const Ly1 = roundTo((Az1 * 10_000) / Lx1, 10);
    const Lx2 = roundTo(side2_m * 100, 10);
    const Ly2 = roundTo(side2_m * 100, 10);
    const Hf = 60;
    result = {
      fields: [
        ['Az₁ (medianera) requerida', `${Az1.toFixed(2)} m²`],
        ['Zapata 1 (Lx1 × Ly1)', `${Lx1} × ${Ly1} cm`],
        ['Az₂ (interior) requerida', `${Az2.toFixed(2)} m²`],
        ['Zapata 2 (Lx2 × Ly2)', `${Lx2} × ${Ly2} cm`],
        ['Hf', `${Hf} cm`],
        ['Viga de conexión bb × hb', `30 × 50 cm (estimado)`],
      ],
      mainResult: `Z1: ${Lx1}×${Ly1} cm · Z2: ${Lx2}×${Ly2} cm · Viga 30×50`,
      comments: [
        'La medianera se alarga en la dirección perpendicular al lindero (más corta hacia el lindero) para reducir su excentricidad.',
        'La viga de conexión debe diseñarse para el momento Mu = Pu_medianera · e₁.',
      ],
      dims: { Lx: Lx1, Ly: Ly1, Hf, bc, L_cols: 400, Lx2, Ly2 },
    };
  }

  const exportInputs = {
    footing_variant: variant,
    P_servicio: P, P2_servicio: P2,
    sigma_adm, bc, hc, L_cols,
  };
  const [view, setView] = useState<'2D' | '3D'>('2D');

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex justify-end">
        <PredimToolbar
          family="footings"
          variant={variant}
          inputs={exportInputs}
          existing={existing}
        />
      </div>
      <PredimCard title={preset.label} subtitle={preset.descr}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Group title={variant === 'strip' ? 'Carga por metro' : 'Cargas en servicio'}>
            <NumberRow
              label={
                variant === 'strip' ? 'P por metro de muro' :
                variant === 'isolated' ? 'P (columna)' :
                variant === 'combined' ? 'P₁ (columna 1)' :
                'P_medianera'
              }
              value={P}
              onChange={setP}
              step={1000}
              unit={variant === 'strip' ? 'kgf/m' : 'kgf'}
            />
            {(variant === 'combined' || variant === 'connected') && (
              <NumberRow
                label={variant === 'combined' ? 'P₂ (columna 2)' : 'P_interior'}
                value={P2}
                onChange={setP2}
                step={1000}
                unit="kgf"
              />
            )}
            {variant === 'combined' && (
              <NumberRow
                label="L_cols (centro a centro)"
                value={L_cols}
                onChange={setLCols}
                step={25}
                unit="cm"
              />
            )}
          </Group>

          <Group title="Suelo y columna">
            <NumberRow label="σ_adm" value={sigma_adm} onChange={setSigmaAdm} step={0.1} unit="kgf/cm²" />
            {variant === 'isolated' && (
              <>
                <NumberRow label="bc — columna" value={bc} onChange={setBc} step={5} unit="cm" />
                <NumberRow label="hc — columna" value={hc} onChange={setHc} step={5} unit="cm" />
              </>
            )}
          </Group>
        </div>

        <FormulaNote>
          <span className="font-mono">Az_req = P_servicio / σ_adm</span> &nbsp;·&nbsp;
          σ_adm en kgf/cm² convierte a kgf/m² × 10000.
        </FormulaNote>
      </PredimCard>

      <PredimCard title="Resultados">
        <div className="space-y-1">
          {result.fields.map(([label, value]) => (
            <ResultRow key={label} label={label} value={value} />
          ))}
          <div className="mt-3 rounded-[var(--radius-sm)] border p-2.5 text-[12.5px] font-mono"
            style={{
              background: 'rgba(245,158,11,0.10)',
              borderColor: 'rgba(245,158,11,0.40)',
              color: 'var(--accent)',
              fontWeight: 700,
            }}>
            ⤧ {result.mainResult}
          </div>
        </div>

        <div className="mt-3">
          <DiagramPanel
            title={view === '3D' ? 'Vista 3D' : 'Planta + Elevación'}
            toolbar={<View2D3DToggle value={view} onChange={setView} />}
          >
            {view === '3D' ? (
              <div style={{ width: 600, height: 360 }}>
                <Footing3D
                  variant={variant}
                  Lx={result.dims.Lx}
                  Ly={result.dims.Ly}
                  Hf={result.dims.Hf}
                  bc={result.dims.bc ?? 30}
                  hc={result.dims.hc ?? 30}
                  L_cols={result.dims.L_cols}
                  Lx2={result.dims.Lx2}
                  Ly2={result.dims.Ly2}
                />
              </div>
            ) : (
              <FootingDiagram
                variant={variant}
                Lx={result.dims.Lx}
                Ly={result.dims.Ly}
                Hf={result.dims.Hf}
                bc={result.dims.bc}
                hc={result.dims.hc}
                L_cols={result.dims.L_cols}
                Lx2={result.dims.Lx2}
                Ly2={result.dims.Ly2}
              />
            )}
          </DiagramPanel>
        </div>

        <ul className="mt-3 space-y-1 text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          {result.comments.map((c, i) => (
            <li key={i}>• {c}</li>
          ))}
        </ul>

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Para diseño completo con cargas factoradas, cortante, punzonado y flexión, usa el módulo <strong>Zapatas</strong>.
        </div>
      </PredimCard>
    </div>
  );
}
